import dynamic from 'next/dynamic';
import { Game } from './layouts/default/default-game';
import type { DailyJackpotData, DailyJackpotPrimary } from '@/types/zones/slices/daily-jackpot';
import type { PrismicSlicePrimary } from '@/types/prismic/common';
import type { GameType } from '@/types/games';
import type { GameViewModel } from '@/types/games/game-view-model';
import { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import type { SwjTileProps } from '@/logic/games/resolve-swj-tile-props';

const AnimatedTrailerGame = dynamic(() =>
    import('./layouts/animated-trailer/animated-trailer').then((mod) => mod.AnimatedTrailerGame)
);

const TrendingGame = dynamic(() => import('./layouts/trending/trending-game').then((mod) => mod.TrendingGame));

const DailyJackpotGame = dynamic(() =>
    import('./layouts/daily-jackpot/daily-jackpot-game').then((mod) => mod.DailyJackpotGame)
);

const SimplifiedRowGame = dynamic(() =>
    import('./layouts/simplified-row/simplified-row-game').then((mod) => mod.SimplifiedRowGame)
);

const GameBadgeImage = dynamic(() =>
    import('@/app/components/game-badge-image/game-badge-image').then((mod) => mod.GameBadgeImage)
);

export type GameFactoryProps = {
    index?: number;
    gameType?: GameType;
    /** Adapted game view model — raw Prismic documents should be adapted before reaching this component. */
    game: GameViewModel;
    zoneUid?: string;
    zoneData?: PrismicSlicePrimary;
    hideGameInfo?: boolean;
    forceShowGameInfo?: boolean;
    hasFavouriteButton?: boolean;
    priority?: boolean;
    layout?: PRISMIC_ZONE_LAYOUT;
    tracking: {
        gamePosition: number | null;
        rankOrder?: number | string | null;
        triggerGameName?: string | null;
        internalCode?: string | null;
    };
    /** Daily-jackpot zones pass jackpot slice data separately from the adapted game. */
    dailyJackpotData?: DailyJackpotData;
    forceTextBadge?: boolean;
    triggerTrackEvent?: (gameId?: string | undefined) => void;
};

type GameFactoryRenderContext = {
    isLoggedIn: boolean;
    swj: SwjTileProps;
};

export const GameFactoryCore = ({
    index,
    gameType,
    game,
    zoneUid,
    zoneData,
    hideGameInfo,
    forceShowGameInfo,
    hasFavouriteButton,
    priority,
    tracking,
    dailyJackpotData,
    isLoggedIn,
    swj: { showSwjVisuals, swjLabel },
    forceTextBadge,
    triggerTrackEvent
}: GameFactoryProps & GameFactoryRenderContext) => {
    switch (gameType) {
        case 'animated':
            return (
                <AnimatedTrailerGame
                    uid={game.uid}
                    animatedTileVideoUrl={game.animatedTileVideoUrl ?? null}
                    videoId={`${zoneUid}-${game.name.replace(/ /g, '-')}`}
                    src={game.videoTrailerUrl}
                    title={game.name}
                    info={game.description}
                    launcherUrlProps={game.launcherUrlProps}
                    label={game.label}
                    poster={game.mergedImageUrl}
                    hideGameInfo={hideGameInfo}
                    tracking={{
                        gameUid: game.uid,
                        gamePosition: tracking.gamePosition,
                        gameName: game.name,
                        gameProvider: game.providerName
                    }}
                />
            );
        case 'trending':
            return (
                <TrendingGame
                    {...game}
                    index={index!}
                    hideGameInfo={isLoggedIn}
                    swjLabel={swjLabel}
                    isSiteWideJackpot={showSwjVisuals}
                    tracking={{
                        gameUid: game.uid,
                        gamePosition: tracking.gamePosition,
                        gameName: game.name,
                        gameProvider: game.providerName
                    }}
                />
            );
        case 'daily-jackpot':
            return (
                <DailyJackpotGame
                    {...game}
                    gameProps={dailyJackpotData!}
                    zoneData={zoneData as DailyJackpotPrimary}
                    tracking={{
                        gameUid: game.uid,
                        gamePosition: tracking.gamePosition,
                        gameName: game.name,
                        gameProvider: game.providerName
                    }}
                />
            );
        case 'simplified-row':
            return (
                <SimplifiedRowGame
                    game={game}
                    hideGameInfo={hideGameInfo}
                    isSiteWideJackpot={showSwjVisuals}
                    swjLabel={swjLabel}
                    forceTextBadge={forceTextBadge}
                    tracking={{
                        gameUid: game.uid,
                        gamePosition: tracking.gamePosition,
                        gameName: game.name,
                        gameProvider: game.providerName
                    }}
                />
            );
        case 'badge-image':
            return (
                <GameBadgeImage
                    logoUrl={game.logoUrl}
                    backgroundUrl={game.backgroundUrl}
                    customBackgroundColor={game.customBackgroundColor}
                    name={game.name}
                    priority={priority}
                    label={game.label}
                    feedsCode={game.feedsCode}
                />
            );
        default:
            return (
                <Game
                    {...game}
                    hideGameInfo={forceShowGameInfo ? false : hideGameInfo || isLoggedIn}
                    hasFavouriteButton={hasFavouriteButton}
                    priority={priority}
                    swjLabel={swjLabel}
                    isSiteWideJackpot={showSwjVisuals}
                    forceTextBadge={forceTextBadge}
                    triggerTrackEvent={triggerTrackEvent}
                    tracking={{
                        gameUid: game.uid,
                        gamePosition: tracking.gamePosition,
                        gameName: game.name,
                        gameProvider: game.providerName,
                        rankOrder: tracking.rankOrder,
                        triggerGameName: tracking.triggerGameName,
                        internalCode: tracking.internalCode
                    }}
                />
            );
    }
};
